import { StaticJsonRpcProvider, TransactionReceipt } from '@ethersproject/providers'
import { TransactionReceiptStatusEnum } from 'config/constants/transactions'
import { Network, Token } from 'config/types'
import { QueryDepositInfo } from 'config/types/transaction'
import { useRequest } from 'hooks/useRequest'
import { useState } from 'react'
import PaymentService from 'services/PaymentService'
import { isOutSideEvmChain as detectOutSideEvmChain } from 'utils/network'
import { getSimplerRpcProvider } from 'utils/providers'
import { HunnyToast } from 'utils/toastify'

export const useFindDeposit = () => {
  const [submiting, setSubmiting] = useState(false)
  const [isError, setIsError] = useState(false)
  const [depositInfo, setDepositInfo] = useState<QueryDepositInfo & { txnHash: string }>(null)
  const { execute } = useRequest()

  const handleQueryDeposit = async (subscribeDepositId: number, transactionHash: string) => {
    const response = await execute(PaymentService.queryDepositByHash(subscribeDepositId))

    if (response?.data) {
      setDepositInfo({ ...response.data, txnHash: transactionHash })

      if (!(response.data?.depositInfo?.currency && response.data?.depositInfo?.network)) {
        setIsError(true)
      }
    } else {
      HunnyToast.error(response.message)
      setIsError(true)
    }

    setSubmiting(false)
  }

  const handleSubscribeDeposit = async ({
    selectedToken,
    selectedNetwork,
    transactionHash,
  }: {
    selectedToken: Token
    selectedNetwork: Network
    transactionHash: string
  }) => {
    const isOutSideEvmChain = detectOutSideEvmChain(selectedNetwork.chainId)

    if (depositInfo) {
      setDepositInfo(null)
    }
    if (!transactionHash) return

    setSubmiting(true)
    setIsError(false)
    const provider: StaticJsonRpcProvider = getSimplerRpcProvider(selectedNetwork.chainId)
    let txnReceiptResponse: TransactionReceipt

    if (!isOutSideEvmChain) {
      try {
        txnReceiptResponse = await provider.getTransactionReceipt(transactionHash)
      } catch (error) {
        setIsError(true)
        setSubmiting(false)
        return
      }
    }

    const isSubscribed =
      (txnReceiptResponse && txnReceiptResponse.status === TransactionReceiptStatusEnum.SUCCESS) || isOutSideEvmChain

    if (isSubscribed) {
      const response = await PaymentService.subscribeDepositByHash(
        selectedToken.code,
        selectedNetwork.chainId,
        transactionHash,
      )

      if (response.data) {
        handleQueryDeposit(response.data.id, transactionHash)
      }
      return
    }

    setIsError(true)
    setSubmiting(false)
  }

  return {
    submiting,
    handleSubscribeDeposit,
    depositInfo,
    isError,
  }
}
